import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
	selector: 'jepto-button',
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		MatIconModule,
		MatMenuModule,
		MatBadgeModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
	],
	templateUrl: './button.component.html',
	styleUrls: ['./button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JeptoButtonComponent implements AfterViewInit {
	@Input() sw: boolean;
	@Input() customClass: boolean;
	@Input() text: string;
	@Input() isLoading: boolean;
	@Input() type: string;
	@Input() size: string;
	@Input() color: string;
	@Input() routerLink: string;
	@Input() icon: string;
	@Input() align: string;
	@Input() disabled: boolean;
	@Input() buttonType: 'button' | 'submit' | 'reset' = 'button';
	@Input() width: string;
	@Input() height: string;
	@Input() light: string;
	@Input() badgeCount: number;
	@Input() isBadgeEnabled: boolean;
	@Input() useMaterialIcon: boolean = true;
	@Input() menuItems: Array<{ name: string; value: string }> = [];
	@Input() testId: string = '';
	@Output() isClicked = new EventEmitter();
	@Output() onGetMenu = new EventEmitter();
	@ViewChild('button', { read: ElementRef })
	button: ElementRef<HTMLButtonElement>;

	constructor() {}

	ngAfterViewInit() {
		const elem = this.button.nativeElement;
		this.addDefaultClass(elem);
		this.addButtonSizeClass(elem, this.size);
		this.addButtonCustomTypeClass(elem, this.type);
		this.setButtonWidth(elem, this.width);
		this.setButtonHeight(elem, this.height);
	}

	addButtonSizeClass(elem, size) {
		const resolved = size || 'medium';
		elem.classList.add('button--' + resolved);
	}

	addDefaultClass(elem) {
		elem.classList.add('button');
	}

	addButtonCustomTypeClass(elem, type) {
		if (type) {
			elem.classList.add('button--' + this.type);
		}
	}

	setButtonWidth(elem: HTMLElement, width: string | number) {
		this.applyCssLength(elem, 'width', width);
	}

	setButtonHeight(elem: HTMLElement, height: string | number) {
		this.applyCssLength(elem, 'height', height);
	}

	/** True when the button must not accept click or keyboard activation. */
	get isDisabled(): boolean {
		return Boolean(this.disabled) || Boolean(this.isLoading);
	}

	/**
	 * Sets inline width/height: finite non-zero numbers get `px`; strings with a trailing CSS unit
	 * (see regex) or `calc()` / `var()` / etc. are unchanged; other strings get `px` appended
	 * (avoids values like `100pxpx`).
	 */
	private applyCssLength(
		elem: HTMLElement,
		property: 'width' | 'height',
		value: string | number | undefined | null
	): void {
		if (value === undefined || value === null || value === '') {
			return;
		}
		if (typeof value === 'number') {
			if (!Number.isFinite(value) || value === 0) {
				return;
			}
			elem.style[property] = `${value}px`;
			return;
		}
		const s = String(value).trim();
		if (s === '') {
			return;
		}
		if (/^(calc|clamp|min|max|var)\(/i.test(s)) {
			elem.style[property] = s;
			return;
		}
		if (/[a-z%]+$/i.test(s)) {
			elem.style[property] = s;
			return;
		}
		elem.style[property] = `${s}px`;
	}

	onClickButton(event: Event): void {
		if (this.isDisabled) {
			event?.preventDefault?.();
			event?.stopPropagation?.();
			return;
		}
		this.isClicked.emit(event);
	}

	select(menu): void {
		this.onGetMenu.emit(menu);
	}

	@HostListener('keydown.enter', ['$event'])
	@HostListener('keydown.space', ['$event'])
	onKeydownWhileInactive(event: KeyboardEvent): void {
		if (this.isDisabled) {
			event.preventDefault();
			event.stopPropagation();
		}
	}
}
